/**
 * Step of MagicAlbumPanel : the album title. The step opens on the WaitingTitles animation, held as
 * long as the suggestions take (the photos still arriving, the templates, the geocoding and Claude),
 * then the picker appears : the proposals of the TitleSuggester service as a vertical list under the
 * field, the first one prefilled until the customer types something else. data = { model, options }.
 */
fx.ns("shared.screens.magic", class TitleStep extends shared.screens.magic.BaseStep {

    constructor()
    {
        super();

        this._chips = [];
        this._done = false;
        this._revealed = false;
    }

    /** The animation stays at least this long, so that a quick answer does not make it flash. */
    get MIN_WAIT()
    {
        return 1200;
    }

    /** Past this, the animation says it is taking longer than expected. */
    get SLOW_WAIT()
    {
        return 10000;
    }

    /** Past this, the picker opens with the titles at hand rather than holding the customer. */
    get MAX_WAIT()
    {
        return 20000;
    }

    create()
    {
        let box = new fx.components.Box("container");
        box.visibleAtStart = false;
        this.box = box;

        this.title = new fx.components.Text({ text : fx.text("Quel titre pour votre album ?"), className : "title center" });
        box.addChild(this.title, { width : "100%", height : 40, top : 0, left : 0 });

        this.text = new fx.components.Text({ text : fx.text("Choisissez une proposition ou saisissez le vôtre."), className : "text center" });
        box.addChild(this.text, { width : "100%", height : 30, top : 45, left : 0 });

        // proposals : MAX_PROPOSALS lines in a light zone as wide as the field, no scroll (css)
        this.suggestions = new fx.components.Box("title-list");
        box.addChild(this.suggestions, { width : this.LIST_WIDTH, height : this.LIST_HEIGHT, top : 165, left : "center" });

        this.input = new fx.components.Input({ placeHolder : fx.text("Le titre de votre album"), maxlength : 60 });
        this.input.addClass("title-input");
        box.addChild(this.input, { width : this.LIST_WIDTH, height : 70, top : 90, left : "center" });

        this.createButton = new fx.components.Button({ label : fx.text("Créer mon album"), type : "filled" });
        box.addChild(this.createButton, { width : 280, height : 44, top : 172 + this.LIST_HEIGHT + 36, left : "center" });

        this.addChild(box, { width : fx.browser.isDesktop ? 640 : "100%", height : 172 + this.LIST_HEIGHT + 90, top : "center", left : "center" });

        // the wait itself, over the picker until the titles are there
        this.waiting = new shared.screens.magic.WaitingTitles(this._photoThumbs(), this.model.photos ? this.model.photos.active.length : 0);
        this.addChild(this.waiting, { width : "100%", height : "100%", top : 0, left : 0 });
    }

    /** Up to three photos of the album for the stack of the animation. */
    _photoThumbs()
    {
        let photos = this.model.photos ? this.model.photos.active : [];

        return (photos.toArray ? photos.toArray() : photos).slice(0, 3).map((photo) => photo.thumb).filter((thumb) => !!thumb);
    }

    get LIST_WIDTH()
    {
        return fx.browser.isDesktop ? 460 : "90%";
    }

    get LIST_HEIGHT()
    {
        return this.MAX_PROPOSALS * 36 + 12;
    }

    // four lines : two titles Claude wrote and two templates (TitleSuggester.PROPOSALS_PER_SOURCE)
    get MAX_PROPOSALS()
    {
        return 4;
    }

    onReady()
    {
        this.on("click", this._onCreate, this.createButton);

        // the photos may still be arriving : the mobile application offers the album as soon as they
        // are picked (BookMain.proposesMagicOnImport), so the animation says what it is waiting for
        // and nothing is asked of the suggestions until the extraction is over
        if (this.photosPending && this.waiting)
            this.waiting.importing();

        this.whenPhotosReady(() => this._onPhotosReady());
    }

    /**
     * Every photo is there : the animation takes up its own messages, with the count and the
     * pictures the import has finally given, and the suggestions start. Their patience only begins
     * here — the wait for the import is none of their doing, and counting it would open the picker
     * on the generic titles before Claude has been asked anything.
     */
    _onPhotosReady()
    {
        if (this.waiting)
            this.waiting.resume(this.model.photos ? this.model.photos.active.length : 0, this._photoThumbs());

        this.delay(this._onSlow, this.SLOW_WAIT);
        this.delay(this._onTooLong, this.MAX_WAIT);

        this._suggest();
    }

    // the answer is late : the animation says so and keeps running
    _onSlow()
    {
        if (!this._revealed && this.waiting)
            this.waiting.slow();
    }

    // too late to keep the customer waiting : the picker opens with the titles at hand
    _onTooLong()
    {
        this._reveal(this._local || []);
    }

    _suggest()
    {
        if (this._done)
            return;

        let result = fx.service("TitleSuggester").suggest(this.model.photos.active);
        this._local = result.local;

        result.remote.then(
            (titles) => this._reveal(titles),
            () => this._reveal(result.local)
        );

        // the album category (a product without theme) : the cover subtitle, and the themes of
        // that tag for the editor
        if (result.category)
            result.category.then((info) => this._onCategory(info), () => {});
    }

    /**
     * The titles are there : the animation finishes filling its bar, then the picker takes its place
     * with a fade up. The animation is held for MIN_WAIT so that a quick answer does not make it flash.
     */
    _reveal(titles)
    {
        if (this._revealed || this.destroyed)
            return;

        let waiting = this.waiting;
        let left = waiting ? Math.max(0, this.MIN_WAIT - waiting.elapsed) : 0;

        if (left > 0)
        {
            this.delay(() => this._reveal(titles), left);
            return;
        }

        this._revealed = true;

        let show = () => {
            if (this.destroyed || !this.box)
                return;

            if (this.waiting)
            {
                this.waiting.destroy();
                this.waiting = null;
            }

            this._showSuggestions(titles || [], true);

            this.box.addClass("revealed");
            this.box.show();

            // the field gets the focus once the picker is there
            this.delay(this._focus, 500);
        };

        if (waiting)
            waiting.finish(show);
        else
            show();
    }

    _onCategory(info)
    {
        // the step may be gone ("Revenir" before the answer) : the album is not the one to theme
        if (this.destroyed || !this.data || !info || !this.options)
            return;

        this.options.subtitle = info.subtitle || "";
        this.options.category = info.folder || "";
        this.options.country = info.country || "";   // a trip in one country : that country's cover

        if (info.themetag && this.model.applyThemeTag)
            this.model.applyThemeTag(info.themetag);

        // stored in the creation : the covers naming the place then those of the category, and
        // the backgrounds and stickers of the category when the album's theme is of another one
        if (this.model.detectAlbumCategory)
            this.model.detectAlbumCategory(info);
    }

    _focus()
    {
        if (this._done || !this.input || !this.input.input)
            return;

        try { this.input.input.focus(); } catch (e) {}
    }

    _showSuggestions(titles, finished)
    {
        // answers arriving after "Revenir" : the boxes are destroyed
        if (this._done || this.destroyed || !this.suggestions || !this.input)
            return;

        for (let i = 0; i < this._chips.length; i++)
            this._chips[i].destroy();
        this._chips = [];

        let typed = this.input.value;
        let automatic = !typed || typed == this._suggested;

        for (let i = 0; i < titles.length && i < this.MAX_PROPOSALS; i++)
        {
            let title = titles[i];
            let label = title.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            let item = new fx.components.Text({ text : label, className : "title-item" });
            this.suggestions.addChild(item);
            this.on("click", () => { this.input.value = title; this._suggested = title; this._focus(); }, item);
            this._chips.push(item);
        }

        if (automatic && titles.length)
        {
            this.input.value = titles[0];
            this._suggested = titles[0];
        }

    }

    _onCreate()
    {
        this._finish((String(this.input.value) || "").trim().slice(0, 60));
    }

    _finish(title)
    {
        if (this._done)
            return;

        this._done = true;
        this.options.title = title;

        this.trigger("next-step");
    }

    destroy()
    {
        // already destroyed (and its properties freed by fx.Disposable) : nothing left to release
        if (this.destroyed || !this._chips)
            return;

        for (let i = 0; i < this._chips.length; i++)
            this._chips[i].destroy();
        this._chips = [];

        super.destroy();
    }
});
